/**
 * Format a number with thousand separators
 */
export const formatNumber = (num: number) => {
  return new Intl.NumberFormat().format(num);
};

/**
 * Format a value as currency
 */
export const formatCurrency = (
  value: number,
  currency = "LKR",
  compact = false
) => {
  const options: Intl.NumberFormatOptions = {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  };

  if (compact) {
    options.notation = "compact";
    options.compactDisplay = "short";
  }

  return new Intl.NumberFormat("en-US", options).format(value);
};

/**
 * Format a date with options
 */
export const formatDate = (
  date: Date | string,
  options?: Intl.DateTimeFormatOptions
) => {
  const defaultOptions: Intl.DateTimeFormatOptions = options || {
    year: "numeric",
    month: "short",
    day: "numeric",
  };

  return new Date(date).toLocaleDateString("en-US", defaultOptions);
};

/**
 * Format property type to display friendly name
 */
export const formatPropertyType = (type?: string | null) => {
  if (!type) {
    return ""; // Return empty string or a default like "N/A" if type is undefined or null
  }
  const typeMap: Record<string, string> = {
    office: "Office Space",
    retail: "Retail Space",
    warehouse: "Warehouse",
    industrial: "Industrial Space",
    land: "Commercial Land",
    mixed_use: "Mixed-Use Building",
    hotel: "Hotel / Resort",
    residential: "Residential Property",
    apartment: "Apartment",
    house: "House",
    villa: "Villa",
  };

  return typeMap[type.toLowerCase()] || type;
};

/**
 * Format listing type to display friendly name
 */
export const formatListingType = (type: string) => {
  const typeMap: Record<string, string> = {
    sale: "For Sale",
    rent: "For Rent",
    lease: "For Lease",
  };

  return typeMap[type.toLowerCase()] || type;
};

/**
 * Format area with proper unit
 */
export const formatArea = (area: number, unit = "sqft") => {
  return `${formatNumber(area)} ${unit}`;
};

/**
 * Format number of bathrooms
 */
export const formatBathrooms = (bathrooms: number) => {
  return bathrooms === 1 ? "1 Bathroom" : `${bathrooms} Bathrooms`;
};

/**
 * Format number of parkings
 */
export const formatParkings = (parkings: number) => {
  return parkings === 1 ? "1 Parking Space" : `${parkings} Parking Spaces`;
};

/**
 * Format listing status to display friendly name
 */
export const formatListingStatus = (status: string) => {
  if (!status) return "";
  return status.charAt(0).toUpperCase() + status.slice(1);
};
